import { css } from "emotion";
import React from "react";
import { Link } from "react-router-dom";
import { useWindowSize } from "@reach/window-size";
import { PRIMARY_COLOR } from "./constants/colors";
import request from "./utils/request";
import Text from "./components/Text";
import Button from "./components/Button";
import Icon from "./components/Icon";
import Divider from "./components/Divider";
import BrandSymbol from "./components/BrandSymbol";
import BrandText from "./components/BrandText";

const BACKGROUND = "#f5f6fa";
const BORDER_COLOR = "rgb(0 0 0 / 12%)";

const TYPEFORM_URL = "https://tryobvious.typeform.com/to/UqZcnYfn";

const Container = ({ children }) => (
  <div className={css({ width: "126rem", maxWidth: "100%", margin: "0 auto" })}>
    {children}
  </div>
);

const Landing = ({ isAuthenticatedUser }) => {
  const emailInputRef = React.useRef();
  const { width: windowWidth } = useWindowSize();

  const handleRequestAccessSubmit = (email) => {
    return request("/users", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    }).finally(() => {
      window.location = TYPEFORM_URL;
    });
  };

  const imagePadding = windowWidth < 620 ? "2rem" : "6rem";

  return (
    <>
      <Header
        showDashboardLink={isAuthenticatedUser}
        focusEmailInput={() => emailInputRef.current?.focus()}
      />

      <Padder>
        <Container>
          <Divider size="2rem" />

          <div
            style={{
              color: PRIMARY_COLOR,
              margin: "0 auto",
              width: windowWidth < 620 ? "20rem" : "22rem",
            }}
          >
            <CurveSvg />
          </div>

          <Divider size="6rem" />

          <div className={css({ textAlign: "center" })}>
            <Text
              block
              color={PRIMARY_COLOR}
              weight="600"
              size="1.5rem"
              lineHeight={1.3}
              style={{
                textTransform: "uppercase",
                letterSpacing: "0.2rem",
                margin: windowWidth < 620 ? "0 0 3rem" : "0 0 4rem",
              }}
            >
              Business finance <Text weight="800">redesigned</Text>
            </Text>
            <Headline
              component="h1"
              size={
                windowWidth < 620
                  ? "4rem"
                  : windowWidth < 1000
                  ? "6.5vw"
                  : "6.6rem"
              }
              margin={windowWidth < 620 ? "0 0 3rem" : "0 0 4rem"}
              lineHeight={windowWidth < 620 ? 1.1 : undefined}
            >
              Your cash, explained.
            </Headline>
            <Paragraph
              align="center"
              size={
                windowWidth < 520
                  ? "1.8rem"
                  : windowWidth < 620
                  ? "2rem"
                  : "2.5rem"
              }
              style={{
                width: "68rem",
                maxWidth: "100%",
                margin: "0 auto",
                marginBottom: windowWidth < 520 ? "4rem" : "5rem",
              }}
            >
              Obvious helps founders streamline cash management and master their
              burn, runway, expenses, and money flow.
            </Paragraph>

            <SignUpForm
              wrap={windowWidth < 520}
              onSubmit={handleRequestAccessSubmit}
              onInputNodeChange={(node) => (emailInputRef.current = node)}
            />
          </div>
        </Container>
      </Padder>

      <Divider size="8rem" />

      <div
        className={css({
          background: BACKGROUND,
          borderRadius: "0.5rem",
          padding: windowWidth > 640 ? "6rem" : "2rem",
        })}
      >
        <Image
          src={require("./assets/landing/dashboard.png")}
          alt="Insights"
          style={{
            display: "block",
            width: "100%",
            maxWidth: "100rem",
            margin: "0 auto",
            boxShadow: "rgb(54 94 185 / 15%) 0 1rem 2rem",
          }}
        />
      </div>

      <Divider size="16rem" />
      <Padder>
        <Container>
          <Section
            variant="centered"
            title="Why wait weeks to answer mission-critical questions?"
            description="Your business changes in real-time — we built a modern tool that keeps pace and makes sure you stay informed."
            content={
              <Image
                src={require("./assets/landing/sync.png")}
                alt="Bank account syncing"
                style={{
                  width: "72rem",
                  maxWidth: "100%",
                  minWidth: "57rem",
                  margin: "0 auto",
                  boxShadow: "none",
                  filter: "drop-shadow(rgba(47, 35, 115, 0.2) 0px 1.2rem 2rem)",
                }}
              />
            }
          />

          <Divider size="18rem" />

          <Section
            variant="left"
            title={
              <>
                Keep a tight grip
                <br />
                on costs
              </>
            }
            description={
              <>
                <Text weight="700" color="black">
                  Burn.
                </Text>{" "}
                Make sure your mental model of costs matches reality. And know
                where to trim when necessary
                <br />
                <br />
                <Text weight="700" color="black">
                  Upcoming expenses.
                </Text>{" "}
                Identify and cancel unwanted costs before they happen.
                <br />
                <br />
                <Text weight="700" color="black">
                  Subscriptions.
                </Text>{" "}
                Track what services you pay for, and who’s accountable.
              </>
            }
            content={
              <div
                style={{
                  position: "relative",
                  height: "70rem",
                  maxHeight: "min(100vw,calc(100vh - 6rem))",
                  width: "120rem",
                  background: BACKGROUND,
                  borderRadius: "2.4rem",
                }}
              >
                <Image
                  src={require("./assets/landing/activity.png")}
                  alt="Bank activity"
                  style={{
                    position: "absolute",
                    bottom: imagePadding,
                    left: imagePadding,
                    width: "auto",
                    height: "56%",
                  }}
                />
                <Image
                  src={require("./assets/landing/upcoming-expenses.png")}
                  alt="Upcoming expenses"
                  style={{
                    position: "absolute",
                    top: imagePadding,
                    left: `calc(${imagePadding} + 10rem)`,
                    width: "auto",
                    height: "65%",
                    boxShadow: "rgb(54 94 185 / 25%) 0 2.6rem 4.8rem",
                  }}
                />
              </div>
            }
          />

          <Divider size="16rem" />

          <Section
            variant="centered"
            title="Outsource routine work to your 24/7 virtual CFO"
            description="Stop digging through transactions manually. Obvious automatically brings insights and important activity to your attention."
            content={
              <Image
                src={require("./assets/landing/insights-items.png")}
                alt="Variance breakdown"
                style={{
                  width: "110rem",
                  maxWidth: "100%",
                  minWidth: "62rem",
                  margin: "0 auto",
                  boxShadow: "none",
                  filter: "drop-shadow(rgba(47 35 100 / 15%) 0px 12px 16px)",
                }}
              />
            }
          />

          <Divider size="16rem" />

          <Section
            variant="right"
            title="Steer your business flawlessly"
            description={
              <>
                <Text weight="700" color="black">
                  Runway in real-time.
                </Text>{" "}
                Keep your base, worst, and growth scenarios top-of-mind at all
                times.
                <br />
                <br />
                <Text weight="700" color="black">
                  Decision impact.
                </Text>{" "}
                Run your startup responsibly. Know when to invest and when to
                hit the brakes.
              </>
            }
            content={
              <div
                style={{
                  position: "relative",
                  height: "70rem",
                  maxHeight: "min(100vw,calc(100vh - 6rem))",
                  width: "120rem",
                  background: BACKGROUND,
                  borderRadius: "2.4rem",
                }}
              >
                <Image
                  src={require("./assets/landing/runway.png")}
                  alt="Cash flow heat map"
                  style={{
                    position: "absolute",
                    top: imagePadding,
                    right: imagePadding,
                    width: "auto",
                    height: `calc(100% - (6rem + (2 * ${imagePadding})))`,
                  }}
                />
                <Image
                  src={require("./assets/landing/planned-cost.png")}
                  alt="Cash overview"
                  style={{
                    position: "absolute",
                    bottom: imagePadding,
                    right: `calc(${imagePadding} + 16rem)`,
                    width: "auto",
                    height: "30%",
                    boxShadow: "rgb(54 94 185 / 25%) 0 2.6rem 4.8rem",
                  }}
                />
              </div>
            }
          />

          <Divider size="12rem" />

          <Section
            variant="left"
            title={
              <>
                Zoom out. Inspect.
                <br />
                Plan ahead.
              </>
            }
            description={
              <>
                <Text weight="700" color="black">
                  See the big picture.
                </Text>{" "}
                Understand how your burn, revenue and bank balance trends over
                time.
                <br />
                <br />
                <Text weight="700" color="black">
                  Analyze variance.
                </Text>{" "}
                Compare two time periods and instantly identify major drivers of
                change.
                <br />
                <br />
                <Text weight="700" color="black">
                  Forecast.
                </Text>{" "}
                See future cash patterns.
              </>
            }
            content={
              <div
                style={{
                  position: "relative",
                  height: "70rem",
                  maxHeight: "min(100vw,calc(100vh - 6rem))",
                  width: "120rem",
                  background: BACKGROUND,
                  borderRadius: "2.4rem",
                  padding: "4rem",
                }}
              >
                <Image
                  src={require("./assets/landing/heat-map.png")}
                  alt="Cash flow heat map"
                  style={{
                    position: "absolute",
                    bottom: imagePadding,
                    left: `calc(2 * ${imagePadding})`,
                    width: "auto",
                    height: "63%",
                  }}
                />
                <Image
                  src={require("./assets/landing/cash-overview.png")}
                  alt="Cash overview"
                  style={{
                    position: "absolute",
                    top: imagePadding,
                    left: imagePadding,
                    width: "auto",
                    height: "63%",
                    boxShadow: "rgb(54 94 185 / 25%) 0 2.6rem 4.8rem",
                  }}
                />
              </div>
            }
          />

          <Divider size="12rem" />

          <Section
            variant="centered"
            title="A user experience that matches your expectations"
            description="Clear, powerful, social, fast — your cash management solution is finally up to speed."
            content={
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${
                    windowWidth < 520 ? 1 : windowWidth < 1000 ? 2 : 3
                  }, minmax(0,1fr))`,
                  gridGap: windowWidth < 1000 ? "2rem" : "3rem",
                }}
              >
                {[
                  {
                    title: "Team collaboration",
                    body:
                      "Track who’s accountable for costs. Keep teams and stakeholders aligned.",
                    icon: "users",
                  },
                  {
                    title: "Real-time Slack alerts",
                    body:
                      "Receive updates, alerts and daily reports directly to your Slack channel.",
                    icon: "slack",
                  },
                  {
                    title: "Automatic categorization",
                    body:
                      "Obvious uses machine learning to categorize transactions as they happen.",
                    icon: "shapes",
                  },
                  {
                    title: "Vendors identified",
                    body:
                      "No more detective work. No more manual, tedious tagging.",
                    icon: "tag",
                  },
                  {
                    title: "Powerful instant search",
                    body:
                      "Search all of your business’s financials in one place.",
                    icon: "search",
                  },
                  {
                    title: "Secure and encrypted",
                    body: "Safety first. Built to protect your financial data.",
                    icon: "lock",
                  },
                ].map(({ title, body, icon }, i) => {
                  const fontSize = windowWidth < 1100 ? "1.8rem" : "2rem";
                  return (
                    <div
                      key={i}
                      style={{
                        width: "100%",
                        background: "white",
                        padding: windowWidth < 600 ? "3rem" : "4rem",
                        borderRadius: "2rem",
                        boxShadow: "0 0.6rem 2.4rem rgb(54 94 235 / 10%)",
                      }}
                    >
                      <div
                        style={{ color: PRIMARY_COLOR, marginBottom: "1.4rem" }}
                      >
                        <Icon name={icon} size="3rem" />
                      </div>
                      <Text block size={fontSize} weight="700" lineHeight={1.8}>
                        {title}
                      </Text>
                      <Text
                        block
                        size={fontSize}
                        weight="500"
                        lineHeight={1.8}
                        color="rgb(0 0 0 / 60%)"
                      >
                        {body}
                      </Text>
                    </div>
                  );
                })}
              </div>
            }
          />

          <Divider size="14rem" />

          <Section
            variant="centered"
            title="Obvious lives on top of your current banking solution"
            description="Connect with your bank account and get started in just a few clicks.  Using open banking APIs."
            content={
              <Image
                src={require("./assets/landing/banks.png")}
                alt="Banks"
                style={{
                  width: "48rem",
                  maxWidth: "100%",
                  margin: "0 auto",
                  boxShadow: "none",
                }}
              />
            }
          />

          <Divider size="18rem" />

          <div
            style={{
              borderTop: `0.1rem solid ${BORDER_COLOR}`,
              position: "relative",
              margin: "4rem 0",
            }}
          >
            <IconLogo
              size="calc(min(12rem, 14vw))"
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateY(-50%) translateX(-50%)",
                boxShadow: "0 1.2rem 4.8rem rgb(54 94 253 / 40%)",
              }}
            />
          </div>

          <Divider size="14rem" />

          <Section
            variant="centered"
            title={
              <>
                Never worry
                <br />
                about runway again.
              </>
            }
            description={
              <>
                Designed for daily use. No jargon, no tables, no squinting.
                <br />
                Get started with Obvious today.
              </>
            }
          />

          <Divider size="4rem" />

          <SignUpForm
            wrap={windowWidth < 520}
            onSubmit={handleRequestAccessSubmit}
          />

          <Divider size="18rem" />

          <div
            style={{
              borderTop: "0.1rem solid",
              borderColor: "rgb(0 0 0 / 10%)",
              padding: "4rem 0",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  windowWidth < 1000 ? "minmax(0,1fr)" : "auto minmax(0,1fr)",
                alignItems: "center",
                gridGap: "4rem",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto auto",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gridGap: "2rem",
                }}
              >
                <IconLogo size="4rem" />
                <Text>Obvious — Business finance redesigned</Text>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: windowWidth < 800 ? "flex-start" : "flex-end",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridAutoFlow: windowWidth < 620 ? "row" : "column",
                    gridGap: windowWidth < 620 ? "1rem" : "4rem",
                  }}
                >
                  {[
                    {
                      label: "Changelog",
                      url: "/changelog",
                    },
                    { label: "Contact", url: "mailto:hello@tryobvious.com" },
                    { label: "Twitter", url: "https://twitter.com/tryobvious" },
                    {
                      label: "LinkedIn",
                      url: "https://www.linkedin.com/company/tryobvious",
                    },
                    // { label: "Privacy" },
                    // { label: "Terms of Service" },
                  ].map(({ label, url }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "rgb(0 0 0 / 60%)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Padder>
    </>
  );
};

const Image = ({ src, style }) => (
  <img
    src={src}
    alt="TODO"
    style={{
      display: "block",
      borderRadius: "0.8rem",
      // boxShadow:
      //   "rgb(54 94 135 / 15%) 0px 0.2rem 0.8rem, rgb(54 94 135 / 15%) 0px 0.1rem 0.3rem",
      boxShadow:
        "rgb(46 87 185 / 20%) 0 0.8rem 2rem 0px, rgb(26 50 106 / 5%) 0px 0.1rem 0.5rem",
      ...style,
    }}
  />
);

const CurveSvg = () => (
  <svg
    width="200"
    height="34"
    viewBox="0 0 200 34"
    fill="none"
    style={{ width: "100%", height: "auto" }}
  >
    <line
      opacity="0.15"
      y1="16.5"
      x2="200"
      y2="16.5"
      stroke="black"
      strokeDasharray="3 3"
    />
    <path
      d="M25 17.0001L25.2389 17.3L25.4777 17.5999L25.7166 17.8995L25.9554 18.1988L26.1943 18.4976L26.4331 18.7958L26.672 19.0932L26.9108 19.3899L27.1497 19.6855L27.3885 19.9801L27.6274 20.2736L27.8662 20.5657L28.1051 20.8563L28.3439 21.1455L28.5828 21.4329L28.8217 21.7186L29.0605 22.0024L29.2994 22.2842L29.5382 22.5639L29.7771 22.8414L30.0159 23.1166L30.2548 23.3892L30.4936 23.6594L30.7325 23.9268L30.9713 24.1915L31.2102 24.4534L31.449 24.7122L31.6879 24.968L31.9268 25.2205L32.1656 25.4698L32.4045 25.7157L32.6433 25.9581L32.8822 26.1969L33.121 26.4321L33.3599 26.6635L33.5987 26.891L33.8376 27.1145L34.0764 27.334L34.3153 27.5494L34.5541 27.7606L34.793 27.9674L35.0318 28.1699L35.2707 28.3679L35.5096 28.5613L35.7484 28.7501L35.9873 28.9343L36.2261 29.1136L36.465 29.2881L36.7038 29.4577L36.9427 29.6223L37.1815 29.7819L37.4204 29.9363L37.6592 30.0856L37.8981 30.2296L38.1369 30.3684L38.3758 30.5018L38.6146 30.6298L38.8535 30.7523L39.0924 30.8693L39.3312 30.9808L39.5701 31.0867L39.8089 31.187L40.0478 31.2816L40.2866 31.3705L40.5255 31.4536L40.7643 31.531L41.0032 31.6025L41.242 31.6682L41.4809 31.7281L41.7197 31.782L41.9586 31.83L42.1975 31.8721L42.4363 31.9083L42.6752 31.9385L42.914 31.9627L43.1529 31.9809L43.3917 31.9932L43.6306 31.9994L43.8694 31.9996L44.1083 31.9939L44.3471 31.9821L44.586 31.9644L44.8248 31.9406L45.0637 31.9109L45.3025 31.8752L45.5414 31.8336L45.7803 31.7861L46.0191 31.7326L46.258 31.6732L46.4968 31.608L46.7357 31.5369L46.9745 31.46L47.2134 31.3773L47.4522 31.2889L47.6911 31.1948L47.9299 31.0949L48.1688 30.9895L48.4076 30.8784L48.6465 30.7618L48.8854 30.6397L49.1242 30.5121L49.3631 30.3792L49.6019 30.2409L49.8408 30.0972L50.0796 29.9484L50.3185 29.7943L50.5573 29.6352L50.7962 29.471L51.035 29.3018L51.2739 29.1277L51.5127 28.9487L51.7516 28.765L51.9904 28.5765L52.2293 28.3834L52.4682 28.1858L52.707 27.9837L52.9459 27.7772L53.1847 27.5664L53.4236 27.3513L53.6624 27.1322L53.9013 26.9089L54.1401 26.6817L54.379 26.4507L54.6178 26.2158L54.8567 25.9773L55.0955 25.7351L55.3344 25.4895L55.5732 25.2405L55.8121 24.9882L56.051 24.7327L56.2898 24.4741L56.5287 24.2125L56.7675 23.948L57.0064 23.6808L57.2452 23.4108L57.4841 23.1384L57.7229 22.8634L57.9618 22.5861L58.2006 22.3066L58.4395 22.025L58.6783 21.7413L58.9172 21.4557L59.1561 21.1684L59.3949 20.8794L59.6338 20.5889L59.8726 20.2969L60.1115 20.0036L60.3503 19.709L60.5892 19.4135L60.828 19.1169L61.0669 18.8195L61.3057 18.5214L61.5446 18.2226L61.7834 17.9234L62.0223 17.6238L62.2611 17.3239L62.5 17.024L62.7389 16.724L62.9777 16.4241L63.2166 16.1244L63.4554 15.8251L63.6943 15.5263L63.9331 15.2281L64.172 14.9305L64.4108 14.6338L64.6497 14.3381L64.8885 14.0434L65.1274 13.7499L65.3662 13.4577L65.6051 13.1669L65.844 12.8776L66.0828 12.59L66.3217 12.3042L66.5605 12.0202L66.7994 11.7382L67.0382 11.4584L67.2771 11.1807L67.5159 10.9054L67.7548 10.6325L67.9936 10.3622L68.2325 10.0945L68.4713 9.82955L68.7102 9.5675L68.949 9.30842L69.1879 9.05241L69.4268 8.79958L69.6656 8.55003L69.9045 8.30387L70.1433 8.06118L70.3822 7.82207L70.621 7.58662L70.8599 7.35495L71.0987 7.12713L71.3376 6.90326L71.5764 6.68343L71.8153 6.46772L72.0541 6.25623L72.293 6.04903L72.5318 5.84622L72.7707 5.64787L73.0096 5.45405L73.2484 5.26486L73.4873 5.08036L73.7261 4.90063L73.965 4.72573L74.2038 4.55575L74.4427 4.39075L74.6815 4.23078L74.9204 4.07593L75.1592 3.92624L75.3981 3.78179L75.6369 3.64262L75.8758 3.50879L76.1147 3.38036L76.3535 3.25738L76.5924 3.1399L76.8312 3.02796L77.0701 2.9216L77.3089 2.82088L77.5478 2.72583L77.7866 2.63649L78.0255 2.5529L78.2643 2.47508L78.5032 2.40307L78.742 2.33691L78.9809 2.2766L79.2197 2.22219L79.4586 2.17369L79.6975 2.13111L79.9363 2.09449L80.1752 2.06383L80.414 2.03914L80.6529 2.02043L80.8917 2.00772L81.1306 2.001L81.3694 2.00029L81.6083 2.00557L81.8471 2.01685L82.086 2.03412L82.3248 2.05738L82.5637 2.08662L82.8025 2.12182L83.0414 2.16298L83.2803 2.21006L83.5191 2.26307L83.758 2.32197L83.9968 2.38673L84.2357 2.45735L84.4745 2.53378L84.7134 2.616L84.9522 2.70397L85.1911 2.79766L85.4299 2.89703L85.6688 3.00204L85.9076 3.11265L86.1465 3.22881L86.3854 3.35049L86.6242 3.47762L86.8631 3.61016L87.1019 3.74806L87.3408 3.89126L87.5796 4.0397L87.8185 4.19332L88.0573 4.35207L88.2962 4.51588L88.535 4.68468L88.7739 4.8584L89.0127 5.03699L89.2516 5.22035L89.4904 5.40843L89.7293 5.60115L89.9682 5.79842L90.207 6.00018L90.4459 6.20634L90.6847 6.41681L90.9236 6.63152L91.1624 6.85037L91.4013 7.07328L91.6401 7.30017L91.879 7.53093L92.1178 7.76548L92.3567 8.00373L92.5955 8.24557L92.8344 8.49092L93.0733 8.73966L93.3121 8.99172L93.551 9.24697L93.7898 9.50533L94.0287 9.76668L94.2675 10.0309L94.5064 10.298L94.7452 10.5677L94.9841 10.84L95.2229 11.1147L95.4618 11.3918L95.7006 11.6712L95.9395 11.9527L96.1783 12.2362L96.4172 12.5216L96.6561 12.8088L96.8949 13.0976L97.1338 13.3881L97.3726 13.68L97.6115 13.9732L97.8503 14.2676L98.0892 14.5631L98.328 14.8596L98.5669 15.1569L98.8057 15.455L99.0446 15.7537L99.2834 16.0529L99.5223 16.3525L99.7612 16.6523L100 16.9523L100.239 17.2523L100.478 17.5522L100.717 17.8518L100.955 18.1512L101.194 18.45L101.433 18.7483L101.672 19.0459L101.911 19.3427L102.15 19.6385L102.389 19.9333L102.627 20.2269L102.866 20.5192L103.105 20.8101L103.344 21.0995L103.583 21.3873L103.822 21.6732L104.061 21.9574L104.299 22.2395L104.538 22.5195L104.777 22.7974L105.016 23.0729L105.255 23.346L105.494 23.6165L105.732 23.8844L105.971 24.1496L106.21 24.4119L106.449 24.6712L106.688 24.9274L106.927 25.1805L107.166 25.4303L107.404 25.6768L107.643 25.9197L107.882 26.1591L108.121 26.3949L108.36 26.6269L108.599 26.855L108.838 27.0792L109.076 27.2993L109.315 27.5154L109.554 27.7272L109.793 27.9347L110.032 28.1379L110.271 28.3366L110.51 28.5308L110.748 28.7204L110.987 28.9052L111.226 29.0854L111.465 29.2606L111.704 29.431L111.943 29.5964L112.182 29.7568L112.42 29.9121L112.659 30.0622L112.898 30.207L113.137 30.3466L113.376 30.4809L113.615 30.6097L113.854 30.7331L114.092 30.8511L114.331 30.9635L114.57 31.0703L114.809 31.1714L115.048 31.2669L115.287 31.3567L115.525 31.4408L115.764 31.5191L116.003 31.5915L116.242 31.6582L116.481 31.7189L116.72 31.7738L116.959 31.8228L117.197 31.8658L117.436 31.9029L117.675 31.9341L117.914 31.9592L118.153 31.9784L118.392 31.9916L118.631 31.9988L118.869 32L119.108 31.9952L119.347 31.9844L119.586 31.9676L119.825 31.9448L120.064 31.9161L120.303 31.8813L120.541 31.8406L120.78 31.794L121.019 31.7415L121.258 31.6831L121.497 31.6188L121.736 31.5486L121.975 31.4726L122.213 31.3909L122.452 31.3034L122.691 31.2101L122.93 31.1112L123.169 31.0067L123.408 30.8965L123.647 30.7808L123.885 30.6595L124.124 30.5328L124.363 30.4007L124.602 30.2632L124.841 30.1205L125.08 29.9724L125.318 29.8192L125.557 29.6609L125.796 29.4975L126.035 29.3291L126.274 29.1557L126.513 28.9775L126.752 28.7945L126.99 28.6068L127.229 28.4145L127.468 28.2176L127.707 28.0162L127.946 27.8104L128.185 27.6002L128.424 27.3859L128.662 27.1673L128.901 26.9447L129.14 26.7182L129.379 26.4877L129.618 26.2535L129.857 26.0155L130.096 25.7739L130.334 25.5289L130.573 25.2804L130.812 25.0286L131.051 24.7736L131.29 24.5155L131.529 24.2544L131.768 23.9903L132.006 23.7235L132.245 23.454L132.484 23.1819L132.723 22.9074L132.962 22.6304L133.201 22.3513L133.439 22.0699L133.678 21.7866L133.917 21.5013L134.156 21.2143L134.395 20.9255L134.634 20.6352L134.873 20.3435L135.111 20.0504L135.35 19.756L135.589 19.4606L135.828 19.1642L136.067 18.8669L136.306 18.5689L136.545 18.2702L136.783 17.9711L137.022 17.6715L137.261 17.3717L137.5 17.0717L137.739 16.7717L137.978 16.4718L138.217 16.1721L138.455 15.8728L138.694 15.5739L138.933 15.2755L139.172 14.9779L139.411 14.681L139.65 14.3851L139.889 14.0903L140.127 13.7966L140.366 13.5041L140.605 13.2131L140.844 12.9236L141.083 12.6357L141.322 12.3496L141.561 12.0653L141.799 11.783L142.038 11.5028L142.277 11.2248L142.516 10.9491L142.755 10.6758L142.994 10.405L143.232 10.1369L143.471 9.87156L143.71 9.60904L143.949 9.34947L144.188 9.09297L144.427 8.83963L144.666 8.58956L144.904 8.34284L145.143 8.09959L145.382 7.8599L145.621 7.62387L145.86 7.39159L146.099 7.16315L146.338 6.93864L146.576 6.71816L146.815 6.5018L147.054 6.28963L147.293 6.08174L147.532 5.87822L147.771 5.67916L148.01 5.48461L148.248 5.29468L148.487 5.10943L148.726 4.92893L148.965 4.75326L149.204 4.58249L149.443 4.41669L149.682 4.25592L149.92 4.10025L150.159 3.94973L150.398 3.80444L150.637 3.66443L150.876 3.52975L151.115 3.40045L151.354 3.2766L151.592 3.15824L151.831 3.04541L152.07 2.93817L152.309 2.83654L152.548 2.74059L152.787 2.65034L153.025 2.56582L153.264 2.48709L153.503 2.41415L153.742 2.34705L153.981 2.28581L154.22 2.23046L154.459 2.18101L154.697 2.1375L154.936 2.09992L155.175 2.06831L155.414 2.04267L155.653 2.02301L155.892 2.00934L156.131 2.00167L156.369 2L156.608 2.00433L156.847 2.01465L157.086 2.03097L157.325 2.05328L157.564 2.08157L157.803 2.11582L158.041 2.15602L158.28 2.20217L158.519 2.25423L158.758 2.31219L158.997 2.37603L159.236 2.44571L159.475 2.52122L159.713 2.60252L159.952 2.68957L160.191 2.78235L160.43 2.88082L160.669 2.98494L160.908 3.09466L161.147 3.20994L161.385 3.33074L161.624 3.45701L161.863 3.58869L162.102 3.72574L162.341 3.8681L162.58 4.01571L162.818 4.16851L163.057 4.32645L163.296 4.48945L163.535 4.65746L163.774 4.83041L164.013 5.00822L164.252 5.19083L164.49 5.37817L164.729 5.57015L164.968 5.7667L165.207 5.96775L165.446 6.17321L165.685 6.383L165.924 6.59704L166.162 6.81524L166.401 7.03751L166.64 7.26377L166.879 7.49392L167.118 7.72788L167.357 7.96554L167.596 8.20682L167.834 8.45161L168.073 8.69982L168.312 8.95136L168.551 9.20611L168.79 9.46398L169.029 9.72486L169.268 9.98866L169.506 10.2553L169.745 10.5246L169.984 10.7964L170.223 11.0708L170.462 11.3475L170.701 11.6265L170.939 11.9077L171.178 12.1909L171.417 12.476L171.656 12.7629L171.895 13.0515L172.134 13.3417L172.373 13.6334L172.611 13.9264L172.85 14.2206L173.089 14.516L173.328 14.8123L173.567 15.1095L173.806 15.4075L174.045 15.7061L174.283 16.0052L174.522 16.3047L174.761 16.6045L175 16.9045"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle
      cx="147"
      cy="7"
      r="5"
      fill="currentColor"
      stroke="white"
      strokeWidth="2"
    />
  </svg>
);

const Headline = ({ children, variant, ...props }) => {
  const { width: windowWidth } = useWindowSize();
  return (
    <Text
      block
      weight="800"
      size={
        variant === "medium"
          ? windowWidth < 520
            ? "2.4rem"
            : windowWidth < 800
            ? "5vw"
            : "4rem"
          : undefined
      }
      lineHeight={1.2}
      {...props}
    >
      {children}
    </Text>
  );
};

const Paragraph = ({ children, variant, ...props }) => {
  const { width: windowWidth } = useWindowSize();

  const sizeByVariant = {
    medium: windowWidth < 620 ? "1.8rem" : "2.2rem",
  };

  return (
    <Text
      block
      size={sizeByVariant[variant]}
      weight="400"
      lineHeight={1.5}
      color="rgb(0 0 0 / 60%)"
      {...props}
    >
      {children}
    </Text>
  );
};

const SignUpForm = ({ wrap, onSubmit, onInputNodeChange }) => {
  const [email, setEmail] = React.useState("");
  const [isPending, setIsPending] = React.useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setIsPending(true);
        onSubmit(email)
          .then(() => setEmail(""))
          .finally(() => setIsPending(false));
      }}
      style={{
        display: "grid",
        gridTemplateColumns: wrap ? undefined : "minmax(0,1fr) auto",
        alignItems: "center",
        justifyContent: wrap ? "stretch" : "center",
        gridGap: "1rem",
        width: "100%",
        maxWidth: "58rem",
        margin: "0 auto",
        padding: wrap ? "0 2rem" : 0,
      }}
    >
      <input
        required
        value={email}
        type="email"
        autoComplete="email"
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your work email"
        className={css({
          flex: "1 1 0%",
          border: "0.1rem solid #ddd",
          borderRadius: "0.5rem",
          height: "5rem",
          lineHeight: "initial",
          fontSize: wrap ? "1.6rem" : "1.8rem",
          fontWeight: "500",
          textAlign: wrap ? "center" : "left",
          padding: "0 1.6rem",
          minWidth: 0,
          ":hover": {
            borderColor: "#ccc",
          },
          "::placeholder": {
            color: "rgb(0 0 0 / 60%)",
          },
        })}
        ref={(node) => {
          if (onInputNodeChange == null) return;
          onInputNodeChange(node);
        }}
      />
      <Button
        type="submit"
        variant="primary"
        size="large"
        style={{
          fontSize: wrap ? "1.6rem" : "1.8rem",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        disabled={isPending}
      >
        <span>Get early access</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          style={{ display: "block", marginLeft: "1rem" }}
        >
          <path
            d="M-0.000156403 7.00008V9.00008H11.9998L6.49984 14.5001L7.91984 15.9201L15.8398 8.00008L7.91984 0.0800781L6.49984 1.50008L11.9998 7.00008H-0.000156403Z"
            fill="currentColor"
          />
        </svg>
      </Button>
    </form>
  );
};

const IconLogo = ({ size, style }) => (
  <div
    style={{
      width: size,
      height: size,
      background: PRIMARY_COLOR,
      borderRadius: `calc(${size} * 0.2)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...style,
    }}
  >
    <div style={{ width: "35%" }}>
      <BrandSymbol color="white" />
    </div>
  </div>
);

const Section = ({ variant, title, description, content }) => {
  const { width: windowWidth } = useWindowSize();

  const baseVariant = ["left", "right"].includes(variant) ? "split" : variant;

  switch (baseVariant) {
    case "centered":
      return (
        <>
          <div style={{ textAlign: "center" }}>
            <Headline
              variant="medium"
              margin="0 auto 3rem"
              windowWidth={windowWidth}
              style={{ maxWidth: "100%", width: "56rem" }}
            >
              {title}
            </Headline>
            <Paragraph
              variant="medium"
              style={{ width: "72rem", maxWidth: "100%", margin: "0 auto" }}
            >
              {description}
            </Paragraph>
          </div>

          {content != null && (
            <>
              <Divider size="5rem" />
              {content}
            </>
          )}
        </>
      );
    case "split": {
      const text = (
        <div
          style={{
            gridArea: `auto / span 7 / auto / auto`,
            padding: windowWidth < 960 ? "4rem 0" : "6rem 0",
            alignSelf: "flex-end",
          }}
        >
          <Headline
            variant="medium"
            margin="0 0 3rem"
            style={{ maxWidth: "100%", width: "56rem" }}
          >
            {title}
          </Headline>
          <Paragraph variant="medium">{description}</Paragraph>
        </div>
      );
      const image = (
        <div
          style={{
            gridArea: "auto / span 9 / auto / auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: variant === "left" ? "flex-start" : "flex-end",
          }}
        >
          {content ?? (
            <div
              style={{
                height: "10vh",
                maxheight: "80rem",
                minheight: "50rem",
                width: "120rem",
                background: BACKGROUND,
                borderradius: "2.4rem",
              }}
            />
          )}
        </div>
      );
      return (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              windowWidth < 960 ? "minmax(0,1fr)" : "repeat(16, minmax(0,1fr))",
            gridGap:
              windowWidth < 960 ? 0 : windowWidth < 1200 ? "4rem" : "6rem",
          }}
        >
          {variant === "left" ? (
            <>
              {text}
              {image}
            </>
          ) : (
            <>
              {image}
              {text}
            </>
          )}
        </div>
      );
    }
    default:
      throw new Error();
  }
};

const useScrollObserver = (onScroll) => {
  const callbackRef = React.useRef();
  React.useEffect(() => {
    callbackRef.current = onScroll;
  });
  React.useEffect(() => {
    const handleScroll = () => callbackRef.current();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
};

const Header = ({ showDashboardLink, focusEmailInput }) => {
  const [isAtTop, setIsAtTop] = React.useState(window.scrollY < 40);

  useScrollObserver(() => {
    if (window.scrollY < 40 && isAtTop) return;
    if (window.scrollY >= 40 && !isAtTop) return;
    setIsAtTop(window.scrollY < 40);
  });

  const { width: windowWidth } = useWindowSize();

  const height = !isAtTop || windowWidth < 500 ? "6rem" : "8rem";

  const scrollToTop = () =>
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    });

  return (
    <>
      <div
        className={css({
          width: "100%",
          position: "fixed",
          top: 0,
          transition: "background 250ms ease",
          background: isAtTop ? "transparent" : "white",
          zIndex: 2,
        })}
      >
        <nav
          className={css({
            width: "100%",
            maxWidth: "130rem",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            padding: "0 2rem",
            transition: "height 250ms ease",
            willChange: "height",
            boxShadow: isAtTop ? 0 : `${BORDER_COLOR} 0 0.1rem 0`,
          })}
          style={{ height }}
        >
          <button
            onClick={scrollToTop}
            style={{ display: "block", cursor: "pointer" }}
          >
            <div
              className={css({
                display: "grid",
                alignItems: "center",
                gridTemplateColumns: "3rem auto",
                gridGap: "1rem",
                svg: { display: "block" },
              })}
            >
              <BrandSymbol color={PRIMARY_COLOR} />
              {windowWidth < 500 ? (
                <div />
              ) : (
                <BrandText color="black" width="8rem" />
              )}
            </div>
          </button>
          <div style={{ flex: "1 1 0%" }}></div>
          <div
            style={{
              opacity: typeof showDashboardLink === "boolean" ? 1 : 0,
              transition: "0.1s opacity",
            }}
          >
            {typeof showDashboardLink !==
            "boolean" ? null : showDashboardLink ? (
              <Button
                component={Link}
                to="/dashboard"
                variant="primary"
                size={windowWidth < 500 ? "small" : undefined}
                style={{ fontSize: "1.5rem" }}
              >
                Go to dashboard &rarr;
              </Button>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/login"
                  variant="transparent"
                  size={windowWidth < 500 ? "small" : undefined}
                  style={{ fontSize: "1.5rem", marginRight: "1rem" }}
                >
                  Log in
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    scrollToTop();
                    window.setTimeout(focusEmailInput, 1000);
                  }}
                  size={windowWidth < 500 ? "small" : undefined}
                  style={{ fontSize: "1.5rem" }}
                >
                  Get Obvious
                </Button>
              </>
            )}
          </div>
        </nav>
      </div>
      <div style={{ height: "8rem" }} />
    </>
  );
};

const Padder = ({ children }) => {
  const { width: windowWidth } = useWindowSize();
  return (
    <div
      className={css({
        padding: `0 ${
          windowWidth < 600 ? "2rem" : windowWidth < 720 ? "4rem" : "6rem"
        }`,
        overflow: "hidden",
      })}
    >
      {children}
    </div>
  );
};

export default Landing;
